import { Link } from 'react-router-dom';
import { Logo } from './Brand';
import { BRIEF_URL, DISCORD_URL, SOCIAL } from './config';
import { useI18n } from './i18n';
import TOOLS from './tools/meta.json';
import { FIRM_PAGES } from './firmPages';
import { COMPARE_PAGES } from './comparePages';

const ICONS = {
  x: <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />,
  instagram: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="12" cy="12" r="4.3" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="17.6" cy="6.4" r="1.4" />
    </>
  ),
  youtube: <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />,
  facebook: <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.971h-1.513c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />,
  discord: <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />,
};

const NAMES = { x: 'X', instagram: 'Instagram', youtube: 'YouTube', facebook: 'Facebook', discord: 'Discord' };

// The site-wide footer for public pages: brand, links, socials and the risk disclaimer.
function SiteFooter() {
  const { t, lang } = useI18n();
  const home = lang === 'es' ? '/es' : '/';
  const blog = lang === 'es' ? '/es/blog' : '/blog';
  const tools = lang === 'es' ? '/es/tools' : '/tools';
  const socials = [['x', SOCIAL.x], ['instagram', SOCIAL.instagram], ['youtube', SOCIAL.youtube], ['facebook', SOCIAL.facebook], ['discord', DISCORD_URL]]
    .filter(([, url]) => url);

  return (
    <footer className="site-footer">
      <div className="site-footer-top">
        <div className="site-footer-brand">
          <Link to={home} className="site-footer-logo">
            <Logo />
            <span>
              <span className="brand-name">Market Matrix</span>
              <span className="brand-tag">{t('Trading Journal')}</span>
            </span>
          </Link>
          <p>{t('The trading journal built for futures prop traders. Protect your account and build the trader behind it.')}</p>
        </div>

        <nav className="site-footer-col" aria-label={t('Product')}>
          <span className="site-footer-heading">{t('Product')}</span>
          <Link to={`${home}#features`}>{t('Features')}</Link>
          <Link to={`${home}#your-day`}>{t('How it works')}</Link>
          <Link to={lang === 'es' ? '/es/my-trading-journey' : '/my-trading-journey'}>{t('My Trading Journey')}</Link>
          <Link to={lang === 'es' ? '/es/pricing' : '/pricing'}>{t('Pricing')}</Link>
          <Link to="/signup">{t('Start free')}</Link>
          {COMPARE_PAGES.map((p) => <Link key={p.slug} to={`${lang === 'es' ? '/es' : ''}/${p.slug}`}>{t('Market Matrix vs {name}', { name: p.name })}</Link>)}
          <Link to="/login">{t('Log in')}</Link>
        </nav>

        <nav className="site-footer-col" aria-label={t('Free calculators')}>
          <span className="site-footer-heading">{t('Free calculators')}</span>
          {TOOLS.map((tool) => <Link key={tool.slug} to={`${tools}/${tool.slug}`}>{tool[lang === 'es' ? 'es' : 'en'].name}</Link>)}
          <Link to={tools}>{t('All calculators')}</Link>
        </nav>

        <nav className="site-footer-col" aria-label={t('Journals by prop firm')}>
          <span className="site-footer-heading">{t('Journals by prop firm')}</span>
          {FIRM_PAGES.map((p) => <Link key={p.slug} to={`${lang === 'es' ? '/es' : ''}/${p.slug}`}>{p.fullName || p.name}</Link>)}
          <Link to={`${lang === 'es' ? '/es' : ''}/prop-firms`}>{t('All prop firms')} &rarr;</Link>
        </nav>

        <nav className="site-footer-col" aria-label={t('Resources')}>
          <span className="site-footer-heading">{t('Resources')}</span>
          <Link to={blog}>{t('Blog')}</Link>
          <Link to={lang === 'es' ? '/es/learn' : '/learn'}>{t('Start here: learn to trade')}</Link>
          <Link to={lang === 'es' ? '/es/guide' : '/guide'}>{t('Free guide')}</Link>
          <Link to={lang === 'es' ? '/es/trading-journal-template' : '/trading-journal-template'}>{t('Free journal template')}</Link>
          <Link to={lang === 'es' ? '/es/report-card' : '/report-card'}>{t('Free trading report card')}</Link>
          {BRIEF_URL && <a href={BRIEF_URL} target="_blank" rel="noopener noreferrer">{t('Free pre-market brief')}</a>}
          {DISCORD_URL && <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">{t('Discord community')}</a>}
          <Link to={lang === 'es' ? '/es/guides/interactive-brokers' : '/guides/interactive-brokers'}>{t('Interactive Brokers sync guide')}</Link>
          <Link to="/contact">{t('Help & contact')}</Link>
        </nav>

        <nav className="site-footer-col" aria-label={t('Legal')}>
          <span className="site-footer-heading">{t('Legal')}</span>
          <Link to="/terms">{t('Terms of Service')}</Link>
          <Link to="/privacy">{t('Privacy Policy')}</Link>
          <Link to="/refunds">{t('Refund Policy')}</Link>
        </nav>
      </div>

      {socials.length > 0 && (
        <div className="site-footer-social">
          {socials.map(([id, url]) => (
            <a key={id} href={url} target="_blank" rel="noopener noreferrer" aria-label={t('Market Matrix on {network}', { network: NAMES[id] })}
              title={NAMES[id]}>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{ICONS[id]}</svg>
            </a>
          ))}
        </div>
      )}

      <div className="site-footer-legal">
        <p>
          {t('Trading futures and other leveraged products involves substantial risk of loss and is not suitable for everyone. Only trade with money you can afford to lose. Market Matrix is a journaling and analysis tool; it does not give trading or investment advice, and past results are not a guarantee of future performance.')}
        </p>
        <p>
          {t("Market Matrix is not affiliated with Topstep, Apex Trader Funding, MyFundedFutures, Lucid Trading, Tradeify, TradeDay, Tradovate, NinjaTrader or any other prop firm or platform. Always confirm your account's status with your firm.")}
        </p>
        <p className="site-footer-copy">© {new Date().getFullYear()} Market Matrix. {t('All rights reserved.')}</p>
      </div>
    </footer>
  );
}

export default SiteFooter;
